import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  deleteIntegration,
  saveOrUpdateIntegration,
  updateIntegrationStatus,
  getActiveWebHook,
  fetchIntegrationByUuid
} from '../../api/IntegrationApi';
import { Agent, Integration, IntegrationStatus, ChannelType } from '../model';
import './IntegrationPage.scss';
import { fetchAllAgents } from '../../api/AgentApi';
import { PageProps } from '../main/App';
import { API_URL } from '../../api/api';

const emptyIntegration: Integration = {
  name: '',
  botToken: '',
  agentUuid: undefined,
  type: ChannelType.TELEGRAM,
  status: IntegrationStatus.INACTIVE,
  secretToken: undefined,
  responseWebhook: undefined,
};

const useIntegrationData = (uuid?: string) => {
  const [integration, setIntegration] = useState<Integration | null>(null);
  const [originalIntegration, setOriginalIntegration] = useState<Integration | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [webHook, setWebHook] = useState<string | null>(null);
  const [agents, setAgents] = useState<Agent[]>([]);
  const isNewIntegration = !uuid;

  useEffect(() => {
    fetchAllAgents()
      .then(setAgents)
      .catch(() => setError('Failed to load agents'));
  }, []);

  useEffect(() => {
    if (!isNewIntegration) {
      fetchIntegrationByUuid(uuid)
        .then(data => {
          setIntegration(data);
          setOriginalIntegration({ ...data });
        })
        .catch(() => setError('Failed to load integration'));
    } else {
      setIntegration(emptyIntegration);
      setOriginalIntegration(emptyIntegration);
    }
  }, [uuid, isNewIntegration]);

  useEffect(() => {
    if (integration?.status === IntegrationStatus.ACTIVE && integration?.type === ChannelType.TELEGRAM && uuid) {
      getActiveWebHook(uuid)
        .then(webhookUrl => setWebHook(JSON.stringify(webhookUrl.result)))
        .catch(() => setError('Failed to load webhook URL'));
    }
  }, [integration?.status, integration?.type, uuid]);

  return { integration, setIntegration, originalIntegration, setOriginalIntegration, error, setError, webHook, agents, isNewIntegration };
};

const IntegrationPage: React.FC<PageProps> = ({ showNotification }) => {
  const { uuid } = useParams<{ uuid: string }>();
  const navigate = useNavigate();
  const {
    integration,
    setIntegration,
    originalIntegration,
    setOriginalIntegration,
    error,
    setError,
    webHook,
    agents,
    isNewIntegration,
  } = useIntegrationData(uuid);

  const isIntegrationChanged = JSON.stringify(integration) !== JSON.stringify(originalIntegration);
  const cannotBeActivated = integration?.status === IntegrationStatus.INACTIVE && isIntegrationChanged;
  const isSaveDisabled = !integration || !isIntegrationChanged || integration.status === IntegrationStatus.ACTIVE;
  const isDeleteDisabled = integration?.status === IntegrationStatus.ACTIVE;

  const validateIntegration = () => {
    if (!integration?.name || !integration?.agentUuid ||
      (integration.type === ChannelType.TELEGRAM && !integration?.botToken) ||
      (integration.type === ChannelType.WEBHOOK && !integration?.responseWebhook)
    ) {
      showNotification('Please fill in all required fields: Name, Bot Token, and Agent');
      return false;
    }
    return true;
  };

  const handleSave = useCallback(async () => {
    if (!validateIntegration()) return;
    try {
      if (integration) {
        const savedIntegration = await saveOrUpdateIntegration(integration);
        setIntegration(savedIntegration);
        setOriginalIntegration(savedIntegration);
        navigate(`/integration/${savedIntegration.uuid}`);
        setError(null);
      }
    } catch {
      setError('Failed to save integration');
    }
  }, [integration, setIntegration, setOriginalIntegration, navigate, setError]);

  const handleToggleStatus = useCallback(async () => {
    if (cannotBeActivated) {
      showNotification('You must save the integration before activating!');
      return;
    }
    try {
      if (integration) {
        const newStatus = integration.status === IntegrationStatus.ACTIVE ? IntegrationStatus.INACTIVE : IntegrationStatus.ACTIVE;
        await updateIntegrationStatus(uuid!, newStatus);
        setOriginalIntegration({ ...integration, status: newStatus })
        setIntegration({ ...integration, status: newStatus });
      }
    } catch {
      setError('Failed to update integration status');
    }
  }, [integration, cannotBeActivated, setIntegration, setError, showNotification, uuid]);

  const handleDelete = useCallback(async () => {
    try {
      await deleteIntegration(uuid!);
      navigate('/integrations');
    } catch {
      setError('Failed to delete integration');
    }
  }, [uuid, navigate, setError]);

  const getStatusBadge = (status?: IntegrationStatus) => {
    switch (status) {
      case IntegrationStatus.ACTIVE:
        return <span className="status-badge active">Active</span>;
      case IntegrationStatus.INACTIVE:
        return <span className="status-badge inactive">Inactive</span>;
      default:
        return <span className="status-badge default">Unknown</span>;
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      showNotification('Secret Token copied to clipboard!');
    }).catch(() => {
      showNotification('Failed to copy Secret Token');
    });
  };

  if (integration === null) return <div>Loading...</div>;

  const webhookResponse = `${API_URL}/external/webhook/${integration.uuid}`;

  return (
    <div className="integration-page">
      <div className="header-container">
        <h1>{isNewIntegration ? 'Create New Integration' : 'Integration Details'}</h1>
        <div className="status-badge-container">{getStatusBadge(integration.status)}</div>
        <div className="buttons-container">
          <button onClick={handleSave} disabled={isSaveDisabled} className="save-button">Save</button>
          {!isNewIntegration && (
            <button onClick={handleDelete} disabled={isDeleteDisabled} className="delete-button">Delete</button>
          )}
        </div>
      </div>
      {error && <div className="error-message">{error}</div>}
      <div className="form-container">
        <label className="form-label">
          Name:
          <input
            type="text"
            value={integration.name}
            onChange={e => setIntegration({ ...integration, name: e.target.value })}
            className="input-field"
            disabled={integration.status === IntegrationStatus.ACTIVE}
          />
        </label>
        <label className="form-label">
          Type:
          <select
            value={integration.type}
            onChange={e => setIntegration({ ...integration, type: e.target.value as ChannelType })}
            className="select-field"
            disabled={!!uuid}
          >
            <option value={ChannelType.TELEGRAM}>Telegram</option>
            <option value={ChannelType.WEBHOOK}>Webhook</option>
          </select>
        </label>
        <label className="form-label">
          Agent:
          <select
            value={integration.agentUuid}
            onChange={e => setIntegration({ ...integration, agentUuid: e.target.value })}
            className="select-field"
            disabled={integration.status === IntegrationStatus.ACTIVE}
          >
            <option key={undefined} value={undefined}>Select an Agent</option>
            {agents.map(agent => (
              <option key={agent.uuid} value={agent.uuid}>{agent.name}</option>
            ))}
          </select>
        </label>
        {integration.type === ChannelType.TELEGRAM && (
          <label className="form-label">
            Bot Token:
            <input
              type="text"
              value={integration.botToken}
              onChange={e => setIntegration({ ...integration, botToken: e.target.value })}
              className="input-field"
              disabled={integration.status === IntegrationStatus.ACTIVE}
            />
          </label>
        )}
        {integration.type === ChannelType.WEBHOOK && (
          <label className="form-label">
            Response Webhook URL (POST):
            <pre>{`\n{\n  "id": "uuid of message",\n  "chatId": "chatId - given by you",\n  "agentUuid": "ai agent uuid",\n  "text": "ai agent text"\n}`}</pre>
            <input
              type="text"
              value={integration.responseWebhook || ''}
              onChange={e => setIntegration({ ...integration, responseWebhook: e.target.value })}
              className="input-field"
              disabled={integration.status === IntegrationStatus.ACTIVE}
            />
          </label>
        )}
        {integration.status === IntegrationStatus.ACTIVE && integration.secretToken && (
          <>
            <label className="form-label">
              <label className="form-label">
                Send messages (POST) to "cometai" exposed webhook (click to copy):
                Example of request:
                <pre>{`\n{\n    "id": "uuid of message",\n    "chatId": "uuid chatId",\n    "from": {\n        "id": "string ID of user",\n        "firstName": "nullable",\n        "lastName": "nullable",\n        "username": "nullable"\n    },\n    "text": "text of message"\n}`}</pre>
              </label>
              <input
                type="text"
                value={webhookResponse}
                readOnly
                className="input-field"
                onClick={() => copyToClipboard(webhookResponse)}
              />
            </label>
            <label className="form-label">
              Secret Token - put in header "X-Signature"(click to copy):
              <input
                type="text"
                value={integration.secretToken}
                readOnly
                className="input-field"
                onClick={() => copyToClipboard(integration.secretToken!)}
              />
            </label>
          </>
        )}
        {!isNewIntegration && (
          <button onClick={handleToggleStatus} className="status-toggle-button">
            {integration.status === IntegrationStatus.ACTIVE ? 'Deactivate' : 'Activate'}
          </button>
        )}
        {integration.status === IntegrationStatus.ACTIVE && webHook && (
          <label className="form-label">
            Webhook URL:
            <textarea
              className="input-field"
              value={webHook || ''}
              readOnly
              rows={10}
            />
          </label>
        )}
      </div>
    </div>
  );
};

export default IntegrationPage;
